import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  Card,
  CardContent,
  Typography,
  Rating,
  Avatar,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Star,
  Edit,
  Delete,
  CheckCircle
} from '@mui/icons-material';
import SectionHeader from '@shared/SectionHeader';
import PaginatedList from '@shared/PaginatedList';
import EmptyState from '@shared/EmptyState';
import { useAuth } from '@contexts/AuthContext';
import { format, parseISO } from 'date-fns';
import { ro } from 'date-fns/locale';

interface Review {
  id: string;
  clinicId: string;
  clinicName: string;
  doctorId: string;
  doctorName: string;
  visitId: string;
  visitDate: string;
  animalName: string;
  rating: number;
  comment?: string;
  anonymous: boolean;
  createdAt: string;
  status: 'pending' | 'approved' | 'rejected';
}

const ReviewsPage: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalReviews, setTotalReviews] = useState(0);

  const fetchReviews = useCallback(async () => {
    if (!user?.id) return;

    setLoading(true);
    setError(null);

    try {
      // TODO: Replace with actual API call
      // const response = await reviewsService.getMyReviews({ page, limit });
      // setReviews(response.data);
      // setTotalReviews(response.pagination.total);

      // Mock data
      const mockReviews: Review[] = [
        {
          id: 'rev1',
          clinicId: 'clinic1',
          clinicName: 'Clinica Veterinară Happy Paws',
          doctorId: 'doc1',
          doctorName: 'Dr. Ion Popescu',
          visitId: 'visit1',
          visitDate: '2024-10-15T10:00:00Z',
          animalName: 'Rex',
          rating: 5,
          comment: 'Experiență excelentă! Doctorul a fost foarte atent și profesionist.',
          anonymous: false,
          createdAt: '2024-10-16T12:00:00Z',
          status: 'approved'
        },
        {
          id: 'rev2',
          clinicId: 'clinic2',
          clinicName: 'Cabinet Veterinar Animalis',
          doctorId: 'doc2',
          doctorName: 'Dr. Maria Ionescu',
          visitId: 'visit2',
          visitDate: '2024-09-20T14:00:00Z',
          animalName: 'Miau',
          rating: 4,
          comment: 'Servicii bune, dar timpul de așteptare a fost lung.',
          anonymous: true,
          createdAt: '2024-09-21T10:00:00Z',
          status: 'approved'
        }
      ];

      setReviews(mockReviews);
      setTotalReviews(mockReviews.length);
    } catch (err) {
      console.error('Error fetching reviews:', err);
      setError('Eroare la încărcarea recenziilor.');
    } finally {
      setLoading(false);
    }
  }, [user?.id, page, limit]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handlePageChange = (value: number) => {
    setPage(value);
  };

  const handleDeleteReview = async (reviewId: string) => {
    if (!window.confirm('Ești sigur că vrei să ștergi această recenzie?')) return;

    try {
      // TODO: Implement API call
      console.log('Deleting review:', reviewId);
      setReviews(prev => prev.filter(r => r.id !== reviewId));
    } catch (err) {
      console.error('Error deleting review:', err);
      setError('Eroare la ștergerea recenziei.');
    }
  };

  const getStatusChip = (status: Review['status']) => {
    switch (status) {
      case 'approved':
        return <Chip label="Aprobată" color="success" size="small" icon={<CheckCircle />} />;
      case 'pending':
        return <Chip label="În așteptare" color="warning" size="small" />;
      case 'rejected':
        return <Chip label="Respinsă" color="error" size="small" />;
      default:
        return null;
    }
  };

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <SectionHeader
        title="Recenziile Mele"
        subtitle="Vizualizează și gestionează recenziile tale pentru vizitele medicale"
        onRefresh={fetchReviews}
      />

      <Box mt={3}>
        <PaginatedList
          currentPage={page}
          totalPages={Math.ceil(totalReviews / limit)}
          totalItems={totalReviews}
          itemsPerPage={limit}
          onPageChange={handlePageChange}
          loading={loading}
          emptyMessage="Nu ai lăsat încă nicio recenzie."
        >
          {reviews.map((review: Review) => (
            <Card key={review.id} sx={{ mb: 2, borderRadius: 3, boxShadow: 1 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box display="flex" gap={2} flex={1}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                      {review.doctorName.charAt(0)}
                    </Avatar>
                    <Box flex={1}>
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        {review.clinicName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Cu {review.doctorName} • Pentru {review.animalName}
                      </Typography>
                      <Box display="flex" gap={1} mt={1}>
                        <Chip
                          label={format(parseISO(review.visitDate), 'd MMM yyyy', { locale: ro })}
                          size="small"
                          variant="outlined"
                        />
                        {getStatusChip(review.status)}
                        {review.anonymous && (
                          <Chip label="Anonim" size="small" variant="outlined" />
                        )}
                      </Box>
                    </Box>
                  </Box>
                  <Box display="flex" gap={1}>
                    <Button
                      size="small"
                      startIcon={<Edit />}
                      sx={{ textTransform: 'none', borderRadius: 2 }}
                    >
                      Editează
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<Delete />}
                      onClick={() => handleDeleteReview(review.id)}
                      sx={{ textTransform: 'none', borderRadius: 2 }}
                    >
                      Șterge
                    </Button>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Rating value={review.rating} readOnly size="small" />
                  <Typography variant="body2" color="text.secondary">
                    {review.rating} din 5
                  </Typography>
                </Box>

                {review.comment && (
                  <Typography variant="body1" color="text.primary">
                    {review.comment}
                  </Typography>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                  Postat la {format(parseISO(review.createdAt), 'd MMMM yyyy, HH:mm', { locale: ro })}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </PaginatedList>
      </Box>

      {reviews.length === 0 && !loading && (
        <EmptyState
          icon={<Star sx={{ fontSize: 60, color: 'text.secondary' }} />}
          title="Nicio recenzie"
          description="După o vizită medicală, vei putea lăsa o recenzie pentru a ajuta alți proprietari de animale."
        />
      )}
    </Container>
  );
};

export default ReviewsPage;

